/**
 * Typed wrappers for the user-history endpoints.
 *
 *   GET    /api/user/threads             → list of conversations
 *   POST   /api/conversation/restore     → load a thread into session
 *   DELETE /api/user/history/<query_id>  → delete a single query
 *
 * The dashboard's History modal consumes /threads (grouped view).
 * Per-query delete is exposed here for completeness but not wired
 * into D5b - defer to D5c if we want a delete-thread flow.
 */
import { api } from './client';

/** A single conversation thread. Mirrors what
 *  query_logger.get_user_threads returns. */
export interface ConversationThread {
  thread_id: string;
  /** Oldest message in the thread - used as the title. */
  first_query: string;
  /** Most recent message - used in some UIs as a "latest" subline. */
  latest_query: string;
  /** Firestore timestamp; serialised as either a string or a
   *  { _seconds, _nanoseconds } shape depending on which path
   *  serialised it. The History modal normalises both. */
  first_timestamp?: unknown;
  latest_timestamp?: unknown;
  turn_count: number;
}

interface ThreadsEnvelope {
  status: 'success';
  threads: ConversationThread[];
  count: number;
  client_id?: string;
}

export async function getThreads(limit = 100): Promise<ConversationThread[]> {
  const env = await api<ThreadsEnvelope>(
    `/api/user/threads?limit=${encodeURIComponent(String(limit))}`,
  );
  return env.threads ?? [];
}

/** Restore a thread into the active chat. Server replaces
 *  session['conversation_history'] with the thread's turns and
 *  sets session['thread_id'] so subsequent /api/chat calls
 *  continue that thread. */
export function restoreThread(threadId: string): Promise<{ status: string }> {
  return api<{ status: string }>('/api/conversation/restore', {
    method: 'POST',
    body: { thread_id: threadId },
  });
}

/** Delete an entire conversation thread (all of its turns)
 *  permanently. The Previous Conversations list confirms before
 *  calling this. */
export function deleteThread(
  threadId: string,
): Promise<{ status: string; deleted_count: number }> {
  return api<{ status: string; deleted_count: number }>(
    `/api/user/thread/${encodeURIComponent(threadId)}`,
    { method: 'DELETE' },
  );
}
